// src/lib/pularCalculoInjecao.ts
//
// Regra do "pular cálculo" quando a conta da Equatorial tem injeção
// (docs/superpowers/specs/2026-10-07-bloqueio-pulo-calculo-injecao-design.md).
//
// Caso que originou: UC 345243701247 (Sicoob Centro-Sul), cuja usina da Vigor fica na
// própria UC. JUL/2026 (fatura 8747) foi pulada como "Não houve injeção" com 2.301 kWh
// injetados e nunca teve boleto. Na auditoria de 07/10/2026, 126 pulos tinham injeção
// de usina da Vigor e 51 nunca viraram boleto.
//
// Situações:
//   A — sem injeção: vale a lista antiga de motivos.
//   B — injeção sem usina da Vigor identificada: só "Injeção só de usina que não é da
//       Vigor", com o número de cada usina digitado pelo analista lendo o PDF.
//   C — injeção de usina da Vigor (lida OU digitada): só os motivos que justificam não
//       cobrar.
//
// O gatilho trg_validar_pulo_calculo repete esta regra no banco: mudou aqui, muda lá.
// A lista de usinas vem de usinas_vigor_numeros() (número longo e curto).

import {
  MOTIVOS_PULAR_CALCULO,
  validarMotivoPularCalculo,
  type CodigoMotivoPularCalculo,
} from './motivoPularCalculo.ts';

export type SituacaoInjecao = 'A' | 'B' | 'C';

type Linha = 1 | 2 | 3 | 4 | 5;
const LINHAS: Linha[] = [1, 2, 3, 4, 5];

/** As colunas de `faturas` que a regra lê. */
export type FaturaInjecao = { [K in `unidade_geradora_${Linha}`]?: string | null }
  & { [K in `injecao_kwh_ug_${Linha}`]?: number | string | null }
  & { energia_injetada_gd1_kwh?: number | string | null; energia_injetada_gd2_kwh?: number | string | null };

/** Número normalizado da usina → nome. Mesmo formato de usinas_vigor_numeros(). */
export type UsinasVigor = ReadonlyMap<string, string>;

/** Número digitado pelo analista. `linha` 1..5 = linha lida da conta; `null` = kWh sem usina lida. */
export interface UsinaDigitada { linha: number | null; numero: string }

export interface UsinaClassificada {
  linha: number | null;
  numero_lido: string | null;
  numero_digitado: string | null;
  /** `null` nas usinas digitadas para os kWh sem usina lida: não dá para repartir os kWh entre elas. */
  kwh: number | null;
  usina_vigor: string | null;
  divergente: boolean;
}

export interface ClassificacaoInjecao {
  situacao: SituacaoInjecao;
  kwh_total: number;
  kwh_vigor: number;
  kwh_outras: number;
  kwh_nao_identificado: number;
  /** kWh da conta que não vieram em nenhuma linha de usina (só nos totais de injeção). */
  kwh_sem_usina_lida: number;
  /** Primeira usina da Vigor achada, lida ou digitada. */
  usina_vigor: string | null;
  usinas: UsinaClassificada[];
}

export interface ReferenciasPulo {
  fatura_ref_id?: number | null;
  parcelamento_id?: string | null;
  autorizado_por_id?: string | null;
  autorizado_por_nome?: string | null;
  confirmou_divergencia?: boolean;
}

/** O que vai em `faturas.pular_calculo_detalhe`. */
export interface DetalhePulo {
  situacao: SituacaoInjecao;
  kwh_total: number;
  kwh_vigor: number;
  kwh_outras: number;
  kwh_nao_identificado: number;
  usinas: UsinaClassificada[];
  descricao: string | null;
  fatura_ref_id: number | null;
  parcelamento_id: string | null;
  autorizado_por_id: string | null;
  autorizado_por_nome: string | null;
  confirmou_divergencia: boolean;
}

export const MOTIVOS_COM_INJECAO_VIGOR: CodigoMotivoPularCalculo[] = [
  'desconto_100_grupo', 'boleto_ja_enviado', 'saldo_em_acordo', 'conta_propria_vigor', 'dispensa_autorizada',
];

export const normalizarNumeroUsina = (v: unknown): string =>
  (typeof v === 'string' || typeof v === 'number' ? String(v) : '').replace(/\D/g, '').replace(/^0+/, '');

export const formatarKwh = (v: number): string =>
  v.toLocaleString('pt-BR', { minimumFractionDigits: Number.isInteger(v) ? 0 : 2, maximumFractionDigits: 2 });

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const arred = (v: number) => Math.round(v * 100) / 100;

export function classificarInjecao(
  f: FaturaInjecao,
  usinas: UsinasVigor,
  digitadas: UsinaDigitada[] = [],
): ClassificacaoInjecao {
  const nomeDe = (numero: string): string | null => (numero ? usinas.get(numero) ?? null : null);
  const lidas = LINHAS
    .map((linha) => ({ linha, numero: f[`unidade_geradora_${linha}`] ?? null, kwh: num(f[`injecao_kwh_ug_${linha}`]) }))
    .filter((l) => l.kwh > 0);
  const somaUg = lidas.reduce((s, l) => s + l.kwh, 0);
  const total = Math.max(somaUg, num(f.energia_injetada_gd1_kwh) + num(f.energia_injetada_gd2_kwh));
  const semUsinaLida = total - somaUg > 0.5 ? total - somaUg : 0;
  const extras = digitadas.filter((d) => d.linha === null && normalizarNumeroUsina(d.numero));

  let kwhVigor = 0;
  let kwhOutras = 0;
  let kwhNaoIdent = 0;
  let usinaVigor: string | null = null;
  const lista: UsinaClassificada[] = [];

  for (const l of lidas) {
    const lido = normalizarNumeroUsina(l.numero);
    const d = digitadas.find((x) => x.linha === l.linha && normalizarNumeroUsina(x.numero));
    const dig = d ? normalizarNumeroUsina(d.numero) : '';
    const nome = nomeDe(lido) ?? nomeDe(dig);
    if (nome) {
      kwhVigor += l.kwh;
      usinaVigor ??= nome;
    } else if (lido || dig) {
      kwhOutras += l.kwh;
    } else {
      kwhNaoIdent += l.kwh;
    }
    lista.push({
      linha: l.linha,
      numero_lido: lido ? String(l.numero).trim() : null,
      numero_digitado: d ? d.numero.trim() : null,
      kwh: arred(l.kwh),
      usina_vigor: nome,
      divergente: !!lido && !!dig && lido !== dig,
    });
  }

  if (semUsinaLida > 0) {
    const nomeExtra = extras.map((e) => nomeDe(normalizarNumeroUsina(e.numero))).find(Boolean) ?? null;
    if (nomeExtra) {
      kwhVigor += semUsinaLida;
      usinaVigor ??= nomeExtra;
    } else if (extras.length) {
      kwhOutras += semUsinaLida;
    } else {
      kwhNaoIdent += semUsinaLida;
    }
    for (const e of extras) {
      lista.push({
        linha: null,
        numero_lido: null,
        numero_digitado: e.numero.trim(),
        kwh: null,
        usina_vigor: nomeDe(normalizarNumeroUsina(e.numero)),
        divergente: false,
      });
    }
  }

  return {
    situacao: total <= 0 ? 'A' : kwhVigor > 0 ? 'C' : 'B',
    kwh_total: arred(total),
    kwh_vigor: arred(kwhVigor),
    kwh_outras: arred(kwhOutras),
    kwh_nao_identificado: arred(kwhNaoIdent),
    kwh_sem_usina_lida: arred(semUsinaLida),
    usina_vigor: usinaVigor,
    usinas: lista,
  };
}

export function motivosPermitidos(situacao: SituacaoInjecao): CodigoMotivoPularCalculo[] {
  if (situacao === 'C') return [...MOTIVOS_COM_INJECAO_VIGOR];
  if (situacao === 'B') return ['injecao_usina_terceiro'];
  return MOTIVOS_PULAR_CALCULO.map((m) => m.codigo).filter((c) => c !== 'injecao_usina_terceiro');
}

/** `null` quando o pulo pode ser gravado; senão, a frase do que falta. */
export function validarPulo(
  c: ClassificacaoInjecao,
  codigo: string | null | undefined,
  descricao: string | null | undefined,
  refs: ReferenciasPulo = {},
): string | null {
  const basico = validarMotivoPularCalculo(codigo, descricao);
  if (basico) return basico;
  const cod = codigo as CodigoMotivoPularCalculo;

  if (!motivosPermitidos(c.situacao).includes(cod)) {
    if (c.situacao === 'A') return 'Esta fatura não tem injeção: "Injeção só de usina que não é da Vigor" não se aplica.';
    if (c.situacao === 'B') {
      return `Esta fatura tem ${formatarKwh(c.kwh_total)} kWh de injeção. Use "Injeção só de usina que não é da Vigor" e digite o número de cada usina lendo o PDF.`;
    }
    return `Esta fatura tem ${formatarKwh(c.kwh_vigor)} kWh de injeção da ${c.usina_vigor}, usina da Vigor. Escolha um dos motivos aceitos para injeção da Vigor.`;
  }

  if (c.situacao === 'B') {
    const faltam = c.usinas.filter((u) => u.linha !== null && !normalizarNumeroUsina(u.numero_digitado));
    if (faltam.length) return `Digite o número da usina da linha ${faltam.map((u) => u.linha).join(', ')} lendo o PDF da conta.`;
    if (c.kwh_sem_usina_lida > 0 && !c.usinas.some((u) => u.linha === null)) {
      return `A conta tem ${formatarKwh(c.kwh_sem_usina_lida)} kWh sem usina lida: digite o número da usina lendo o PDF.`;
    }
    if (c.usinas.some((u) => u.divergente) && !refs.confirmou_divergencia) {
      return 'O número digitado é diferente do que o sistema leu. Confira no PDF e confirme qual está certo.';
    }
  }

  if (cod === 'boleto_ja_enviado' && c.situacao === 'C' && !refs.fatura_ref_id) return 'Escolha a fatura que já tem o boleto deste mês.';
  if (cod === 'saldo_em_acordo' && !refs.parcelamento_id) return 'Escolha o acordo em que o saldo foi incluído.';
  if (cod === 'dispensa_autorizada' && !refs.autorizado_por_id) return 'Escolha quem autorizou a dispensa.';
  return null;
}

export function montarDetalhe(c: ClassificacaoInjecao, refs: ReferenciasPulo, descricao?: string | null): DetalhePulo {
  return {
    situacao: c.situacao,
    kwh_total: c.kwh_total,
    kwh_vigor: c.kwh_vigor,
    kwh_outras: c.kwh_outras,
    kwh_nao_identificado: c.kwh_nao_identificado,
    usinas: c.usinas,
    descricao: (descricao || '').trim() || null,
    fatura_ref_id: refs.fatura_ref_id ?? null,
    parcelamento_id: refs.parcelamento_id ? String(refs.parcelamento_id) : null,
    autorizado_por_id: refs.autorizado_por_id ?? null,
    autorizado_por_nome: refs.autorizado_por_nome ?? null,
    confirmou_divergencia: !!refs.confirmou_divergencia,
  };
}
